import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { StatusBadge } from "../components/StatusBadge";
import { ComprobanteModal } from "../components/ComprobanteModal";
import { ConfirmFlete } from "../components/ConfirmFlete";
import { useAuth } from "../context/AuthContext";
import api, { API, apiErr } from "../lib/api";
import { formatGs, waLink, openWa, MODALIDADES, STATUS_META, avisoEntregaRemitente, pyToday } from "../lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Upload, MessageCircle, Pencil, ExternalLink, Search, X, UserPlus, BadgeDollarSign, Truck, Package, AlertTriangle, PackageOpen } from "lucide-react";
import { IncidenciaModal } from "../components/IncidenciaModal";
import { ResolverIncidenciaModal } from "../components/ResolverIncidenciaModal";
import { useSucursales } from "../lib/useSucursales";
import { toast } from "sonner";
import { CodeChip } from "../components/EnvioDetalle";

const NEXT_STATUS = {
  solicitado: "retirado", retirado: "depositado_origen", depositado_origen: "en_transito",
  en_transito: "en_sucursal_destino",
  en_sucursal_destino: "asignado_repartidor", asignado_repartidor: "en_reparto", en_reparto: "entregado",
};

const empty = { destinatario: "", whatsapp: "", ciudad: "", valor_producto: "", costo_envio: "40000", modalidad: "cod", tipo_servicio: "normal", tipo_paquete: "paquete", horario_entrega: "", contenido: "", remitente_nombre: "", remitente_whatsapp: "", tipo: "unitaria", modo_entrega_origen: "retiro", notas: "", sucursal_origen: "", valor_declarado: "", seguro: false, cliente_id: "" };
const TIPOS_PAQUETE = ["sobre", "paquete", "caja", "atado", "otros"];
const HORARIOS = ["Antes de las 10:30", "Antes de las 12:00", "Antes de las 15:00", "Franja mañana (8:00-12:00)", "Franja tarde (13:00-18:00)"];
const cutoffText = (suc) => (suc === "ASU" ? "11:30" : "14:00");
const dentroHorario = (suc) => {
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  return mins <= (suc === "ASU" ? 11 * 60 + 30 : 14 * 60);
};

export default function Envios({ autoOpen = false, initialTipo = "ocasional" }) {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const isStaff = ["admin", "encargado", "encargado_comisionista", "punto"].includes(user?.role);
  const isDelivery = ["repartidor", "comisionista"].includes(user?.role);
  const sucursales = useSucursales();
  const SUCS = sucursales.filter((s) => s.activa !== false).map((s) => s.code);
  const puntoCodes = sucursales.filter((s) => s.es_punto).map((s) => s.code);
  const [envios, setEnvios] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [success, setSuccess] = useState(null);
  const [declaracion, setDeclaracion] = useState(false);
  const [comp, setComp] = useState(null);
  const [asignarEnv, setAsignarEnv] = useState(null);
  const [cf, setCf] = useState(null);
  const [repartidores, setRepartidores] = useState([]);
  const [clientesReg, setClientesReg] = useState([]);
  const [branch, setBranch] = useState(null);
  const [inc, setInc] = useState(null);
  const [resol, setResol] = useState(null);
  const [cambioReview, setCambioReview] = useState(null);
  const todayStr = pyToday();
  const esEntregaHoy = (e) => e.status === "incidente" && e.incidente?.reagendar_fecha && e.incidente.reagendar_fecha <= todayStr;
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get("/envios").then(({ data }) => setEnvios(data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const interval = setInterval(onFocus, 30000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      clearInterval(interval);
    };
  }, [load]);
  useEffect(() => {
    if (isStaff) {
      api.get("/users").then(({ data }) => setRepartidores(data.filter((u) => ["admin", "encargado", "encargado_comisionista", "repartidor", "comisionista"].includes(u.role)))).catch(() => {});
    }
    if (isStaff || isDelivery) {
      api.get("/users/clientes").then(({ data }) => setClientesReg(data)).catch(() => {});
    }
  }, [isStaff, isDelivery]);

  const doAsignar = async (repartidor_id) => {
    try {
      await api.post(`/envios/${asignarEnv.id}/asignar`, { repartidor_id });
      toast.success("Repartidor asignado"); setAsignarEnv(null); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };

  const canConfirmFlete = (e) => user?.role === "admin" || e.repartidor_retiro_id === user?.id || e.repartidor_id === user?.id || (["encargado", "encargado_comisionista"].includes(user?.role) && user?.sucursal === e.sucursal_origen);
  const canNotify = (e) => e.flete_confirmado && !["solicitado", "entregado", "devuelto", "incidente"].includes(e.status);

  useEffect(() => {
    if (!form.ciudad) { setBranch(null); return; }
    const t = setTimeout(() => {
      api.get(`/sucursales/resolve?ciudad=${encodeURIComponent(form.ciudad)}`)
        .then(({ data }) => setBranch(data.sucursal)).catch(() => setBranch(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.ciudad]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openCreate = (tipo) => { setEditing(null); setDeclaracion(false); setForm({ ...empty, tipo: tipo || (isStaff ? "ocasional" : "unitaria"), sucursal_origen: user?.sucursal || "ASU" }); setShowForm(true); };
  useEffect(() => { if (autoOpen) openCreate(initialTipo); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...empty, destinatario: e.destinatario, whatsapp: e.whatsapp, ciudad: e.ciudad,
      valor_producto: e.valor_producto, costo_envio: e.costo_envio, modalidad: e.modalidad,
      tipo_servicio: e.tipo_servicio || "normal", tipo_paquete: e.tipo_paquete || "paquete",
      contenido: e.contenido || "", sucursal_origen: e.sucursal_origen || "ASU",
      remitente_nombre: e.remitente_nombre || "", remitente_whatsapp: e.remitente_whatsapp || "", notas: e.notas || "",
      valor_declarado: e.valor_declarado || "", seguro: !!e.seguro });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && !declaracion) { toast.error("Debés aceptar la declaración para crear la boleta"); return; }
    setLoading(true);
    try {
      const payload = { ...form, valor_producto: Number(form.valor_producto) || 0, costo_envio: Number(form.costo_envio) || 0, valor_declarado: Number(form.valor_declarado) || 0 };
      if (editing) {
        if (user?.role === "cliente") {
          await api.post(`/envios/${editing.id}/solicitar-cambio`, payload);
          toast.success("Solicitud de cambio enviada. Un administrador debe aprobarla.");
        } else {
          await api.put(`/envios/${editing.id}`, payload);
          toast.success("Boleta actualizada");
        }
        setShowForm(false);
      } else {
        const { data } = await api.post("/envios", payload);
        setShowForm(false);
        setSuccess(data);
      }
      load();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setLoading(false); }
  };

  const advance = async (env) => {
    let next = NEXT_STATUS[env.status];
    if (user?.role === "punto" && env.status === "en_sucursal_destino") next = "entregado";
    if (!next) return;
    if (next === "entregado") { setComp(env); return; }
    try { await api.post(`/envios/${env.id}/status`, { status: next }); toast.success(`Estado: ${STATUS_META[next].label}`); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const confirmDelivery = async (payload) => {
    try {
      const { data } = await api.post(`/envios/${comp.id}/status`, { status: "entregado", ...payload });
      toast.success("Entregado con comprobante");
      if (!avisoEntregaRemitente(data)) toast.info("Entrega registrada. El envío no tiene WhatsApp del remitente para el aviso automático.");
      setComp(null); load();
    }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const incidencia = (env) => setInc(env);

  const CAMBIO_LABELS = { destinatario: "Destinatario", whatsapp: "WhatsApp", ciudad: "Ciudad", valor_producto: "Valor producto", costo_envio: "Flete", modalidad: "Modalidad", tipo_servicio: "Servicio", tipo_paquete: "Paquete", horario_entrega: "Horario", valor_declarado: "Valor declarado", seguro: "Seguro", remitente_whatsapp: "WhatsApp remitente", notas: "Notas" };
  const fmtCambio = (k, v) => (["valor_producto", "costo_envio", "valor_declarado"].includes(k) ? formatGs(Number(v) || 0) : (typeof v === "boolean" ? (v ? "Sí" : "No") : String(v ?? "—")));
  const aprobarCambio = async () => {
    try { await api.post(`/envios/${cambioReview.id}/cambio/aprobar`); toast.success("Cambios aprobados y aplicados"); setCambioReview(null); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };
  const rechazarCambio = async () => {
    try { await api.post(`/envios/${cambioReview.id}/cambio/rechazar`, { motivo: "" }); toast.info("Solicitud de cambio rechazada"); setCambioReview(null); load(); }
    catch (e2) { toast.error(apiErr(e2)); }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("pya_token");
      const res = await fetch(`${API}/envios/bulk`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`${data.creados} boletas creadas${data.errores?.length ? `, ${data.errores.length} con error` : ""}`);
      if (data.errores?.length) data.errores.slice(0, 3).forEach((er) => toast.error(`Línea ${er.linea}: ${er.error}`));
      load();
    } catch (e2) { toast.error(e2.message); }
    e.target.value = "";
  };

  const trackUrl = (code) => `${window.location.origin}/track/${code}`;
  const [retiroModal, setRetiroModal] = useState(null); // {env, fueraHorario}
  const [aceptaHorario, setAceptaHorario] = useState(false);
  const [aceptaCosto, setAceptaCosto] = useState(false);

  const abrirSolicitarRetiro = (env) => {
    setAceptaHorario(false);
    setAceptaCosto(false);
    setRetiroModal({ env, fueraHorario: !dentroHorario(env.sucursal_origen) });
  };

  const enviarSolicitudRetiro = () => {
    const env = retiroModal.env;
    const suc = env.sucursal_origen;
    const sucData = sucursales.find((s) => s.code === suc);
    const wa = sucData?.whatsapp || env.sucursal_whatsapp || env.encargado_whatsapp;
    if (!wa) { toast.error(`La sucursal ${suc} no tiene un WhatsApp configurado para recibir solicitudes de retiro. Avisá a un administrador.`); return; }
    const horarioLinea = retiroModal.fueraHorario
      ? `\n⚠️ SOLICITUD FUERA DE HORARIO (corte ${cutoffText(suc)} en ${suc}). El remitente entiende que el retiro puede realizarse al día siguiente.`
      : "";
    const text = `Solicitud de RETIRO a domicilio · Sucursal ${suc} · Boleta ${env.code}\nRemitente: ${env.remitente_nombre || "-"}\nWhatsApp remitente: ${env.remitente_whatsapp || "-"}\nContenido: ${env.contenido || "-"}\nDestino: ${env.destinatario} (${env.ciudad})${horarioLinea}\n✅ El remitente confirmó que tiene el envío listo y acepta pagar el costo del recorrido si hace esperar al repartidor o no puede entregar.\nSeguimiento: ${trackUrl(env.code)}`;
    openWa(waLink(wa, text));
    toast.success("Solicitud de retiro enviada a la sucursal");
    setRetiroModal(null);
  };
  const notify = (env) => {
    const text = `Hola ${env.destinatario}! Tu envío ${env.code} de Pya'elino Courier está en camino a ${env.ciudad}. Seguilo acá: ${trackUrl(env.code)}`;
    openWa(waLink(env.whatsapp, text));
  };
  const notifyRemitenteEntrega = (env) => { avisoEntregaRemitente(env); };

  const filtered = envios.filter((e) =>
    !q || e.code.toLowerCase().includes(q.toLowerCase()) || e.destinatario.toLowerCase().includes(q.toLowerCase()) || (e.ciudad || "").toLowerCase().includes(q.toLowerCase()));

  const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

  return (
    <>
      <Header title="Envíos y Boletas" subtitle="Creación, tracking y ciclo de estados" onMenu={() => setMobileOpen(true)}
        action={
          <div className="flex items-center gap-2">
            <label className="hidden sm:flex items-center gap-2 text-sm font-semibold text-[#0A1F44] border border-slate-300 hover:bg-slate-50 rounded-lg px-3 py-2 cursor-pointer" data-testid="csv-upload-label">
              <Upload className="h-4 w-4" /> CSV
              <input type="file" accept=".csv" className="hidden" onChange={onCsv} data-testid="csv-upload-input" />
            </label>
            <button onClick={() => openCreate()} data-testid="new-envio-btn" className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2">
              <Plus className="h-4 w-4" /> Nueva boleta
            </button>
          </div>
        } />
      <main className="p-4 sm:p-6 lg:p-8 space-y-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input className={`${input} pl-9`} placeholder="Buscar por código, destinatario o ciudad" value={q} onChange={(e) => setQ(e.target.value)} data-testid="envio-search-input" />
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[#64748B] text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Código</th>
                  <th className="text-left font-semibold px-4 py-3">Destinatario</th>
                  <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Ruta</th>
                  <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">Cobro</th>
                  <th className="text-left font-semibold px-4 py-3">Estado</th>
                  <th className="text-right font-semibold px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`envio-row-${e.code}`}>
                    <td className="px-4 py-3"><CodeChip code={e.code} onChanged={load} className="font-mono font-bold text-xs px-2 py-0.5 rounded bg-slate-100 text-[#0A1F44] border border-slate-200" />{e.tipo_servicio === "prioritario" && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">PRIORITARIO</span>}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0A1F44]">{e.destinatario}</div>
                      <div className="text-xs text-[#64748B]">{e.ciudad}</div>
                      {e.tipo_paquete && <span className="inline-block mt-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 capitalize">{e.tipo_paquete}</span>}
                      {e.tipo_servicio === "prioritario" && e.horario_entrega && <div className="text-[11px] font-semibold text-amber-700">⏱ {e.horario_entrega}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs font-medium text-[#64748B]">{e.sucursal_origen} → {e.sucursal_destino || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell font-semibold text-[#0A1F44]">{formatGs(e.cobro_destinatario)}</td>
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {canNotify(e) && (
                          <button onClick={() => notify(e)} title="Avisar por WhatsApp" data-testid={`wa-btn-${e.code}`} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><MessageCircle className="h-4 w-4" /></button>
                        )}
                        {e.status === "entregado" && e.remitente_whatsapp && isStaff && (
                          <button onClick={() => notifyRemitenteEntrega(e)} title="Avisar entrega al remitente" data-testid={`wa-remitente-${e.code}`} className="p-1.5 rounded-lg text-[#0A1F44] hover:bg-slate-100"><MessageCircle className="h-4 w-4" /></button>
                        )}
                        {!e.flete_confirmado && canConfirmFlete(e) && (
                          <button onClick={() => setCf(e)} title="Confirmar flete" data-testid={`flete-btn-${e.code}`} className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1.5 hover:bg-amber-100"><BadgeDollarSign className="h-3.5 w-3.5" /> Flete</button>
                        )}
                        {user?.role === "cliente" && e.status === "solicitado" && e.modo_entrega_origen === "retiro" && (
                          <button onClick={() => abrirSolicitarRetiro(e)} title="Solicitar retiro a domicilio" data-testid={`solicitar-retiro-${e.code}`} className="p-1.5 rounded-lg text-[#F5A623] hover:bg-amber-50"><Truck className="h-4 w-4" /></button>
                        )}
                        <a href={`/track/${e.code}`} target="_blank" rel="noreferrer" title="Tracking" className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><ExternalLink className="h-4 w-4" /></a>
                        {isStaff && user?.role !== "punto" && !puntoCodes.includes(e.sucursal_destino) && (!["entregado", "devuelto", "incidente"].includes(e.status) || esEntregaHoy(e)) && (
                          <button onClick={() => setAsignarEnv(e)} title="Asignar repartidor" data-testid={`assign-btn-${e.code}`} className="p-1.5 rounded-lg text-[#F5A623] hover:bg-amber-50"><UserPlus className="h-4 w-4" /></button>
                        )}
                        {(user?.role === "admin" || (isStaff && !["entregado", "devuelto"].includes(e.status)) || (user?.role === "cliente" && e.created_by === user?.id && !["entregado", "devuelto"].includes(e.status))) && (
                          <button onClick={() => openEdit(e)} title={user?.role === "cliente" ? "Solicitar cambios" : "Editar"} data-testid={`edit-btn-${e.code}`} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                        )}
                        {e.cambio_solicitado?.status === "pendiente" && user?.role === "cliente" && (
                          <span data-testid={`cambio-pendiente-${e.code}`} className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-lg px-2 py-1">Cambio pendiente</span>
                        )}
                        {user?.role === "admin" && e.cambio_solicitado?.status === "pendiente" && (
                          <button onClick={() => setCambioReview(e)} title="Revisar cambios solicitados" data-testid={`revisar-cambio-${e.code}`} className="text-[11px] font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-2 py-1.5">Revisar cambio</button>
                        )}
                        {(isStaff || isDelivery) && NEXT_STATUS[e.status] && (
                          <button onClick={() => advance(e)} data-testid={`advance-btn-${e.code}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#0A1F44] text-white hover:bg-[#132D5C]">{(user?.role === "punto" && e.status === "en_sucursal_destino") ? "Entregado" : STATUS_META[NEXT_STATUS[e.status]].label}</button>
                        )}
                        {["asignado_repartidor", "en_reparto", "en_sucursal_destino"].includes(e.status) && (isStaff || e.repartidor_id === user?.id) && (
                          <button onClick={() => incidencia(e)} title="Reportar incidencia" data-testid={`incidencia-btn-${e.code}`} className="p-1.5 rounded-lg text-white bg-rose-500 hover:bg-rose-600"><AlertTriangle className="h-4 w-4" /></button>
                        )}
                        {["incidente", "almacenado"].includes(e.status) && (isStaff || e.created_by === user?.id) && (
                          <button onClick={() => setResol(e)} title="Resolver incidencia" data-testid={`resolver-btn-${e.code}`} className="p-1.5 rounded-lg text-white bg-teal-600 hover:bg-teal-700"><PackageOpen className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-[#64748B]">No hay envíos todavía. Creá tu primera boleta.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Form modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? (user?.role === "cliente" ? `Solicitar cambios · ${editing.code}` : `Editar ${editing.code}`) : "Nueva boleta"}</DialogTitle></DialogHeader>
          {editing && user?.role === "cliente" && (
            <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2 -mt-1">Los cambios que solicites deben ser aprobados por un administrador antes de aplicarse.</p>
          )}
          <form onSubmit={submit} className="space-y-3">
            {!editing && (isStaff || isDelivery) && (
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Cliente registrado (opcional)</label>
                <select className={input} value={form.cliente_id} data-testid="envio-cliente-select"
                  onChange={(e) => {
                    const cli = clientesReg.find((c) => c.id === e.target.value);
                    setForm({ ...form, cliente_id: e.target.value,
                      remitente_nombre: cli ? cli.name : form.remitente_nombre,
                      remitente_whatsapp: cli?.whatsapp || form.remitente_whatsapp });
                  }}>
                  <option value="">Sin cliente registrado</option>
                  {clientesReg.map((c) => <option key={c.id} value={c.id}>{c.name}{c.whatsapp ? ` · ${c.whatsapp}` : ""}</option>)}
                </select>
                {form.cliente_id && (
                  <p className="text-xs mt-1 font-medium text-emerald-600">La boleta quedará en el panel y estado de cuenta de este cliente.</p>
                )}
              </div>
            )}
            {!editing && (isStaff || isDelivery) && (
              <div className={isStaff ? "grid grid-cols-2 gap-3" : ""}>
                {isStaff && (
                  <div>
                    <label className="text-xs font-semibold text-[#64748B]">Tipo</label>
                    <select className={input} value={form.tipo} onChange={set("tipo")} data-testid="envio-tipo-select">
                      <option value="ocasional">Ocasional (no registrado)</option>
                      <option value="unitaria">Unitaria</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-[#64748B]">Remitente</label>
                  <input className={input} value={form.remitente_nombre} onChange={set("remitente_nombre")} placeholder="Nombre remitente" data-testid="envio-remitente-input" />
                </div>
              </div>
            )}
            {(isStaff || isDelivery) && (
              <div>
                <label className="text-xs font-semibold text-[#64748B]">WhatsApp del remitente</label>
                <input className={input} value={form.remitente_whatsapp} onChange={set("remitente_whatsapp")} placeholder="0981... (recibe avisos de entrega e incidencias)" data-testid="envio-remitente-whatsapp-input" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Destinatario *</label>
              <input className={input} value={form.destinatario} onChange={set("destinatario")} required data-testid="envio-destinatario-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">WhatsApp *</label>
                <input className={input} value={form.whatsapp} onChange={set("whatsapp")} required placeholder="0981..." data-testid="envio-whatsapp-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Ciudad destino *</label>
                <input className={input} value={form.ciudad} onChange={set("ciudad")} required placeholder="Ej. Asunción" data-testid="envio-ciudad-input" />
                {form.ciudad && (
                  <p className={`text-xs mt-1 font-medium ${branch ? "text-emerald-600" : "text-rose-500"}`}>
                    {branch ? `Sucursal: ${branch.code} · ${branch.name}` : "Ciudad no reconocida"}
                  </p>
                )}
                {branch && branch.es_punto && (
                  <div className="mt-1.5 rounded-lg bg-violet-50 border border-violet-200 p-2.5 text-xs text-violet-900 flex gap-2 items-start" data-testid="boleta-aviso-punto">
                    <Package className="h-3.5 w-3.5 mt-0.5 shrink-0 text-violet-500" />
                    <span><b>Solo punto de retiro.</b> En {branch.name} no hay entrega a domicilio: el destinatario retira su encomienda en nuestro Punto Pya'elino.</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Valor producto (Gs.)</label>
                <input type="number" className={input} value={form.valor_producto} onChange={set("valor_producto")} data-testid="envio-valor-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Costo de envío / flete (Gs.)</label>
                <input type="number" className={input} value={form.costo_envio} onChange={set("costo_envio")} data-testid="envio-flete-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Sucursal / ciudad de retiro (origen)</label>
              <select className={input} value={form.sucursal_origen} onChange={set("sucursal_origen")} data-testid="envio-sucursal-origen-select">
                {SUCS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {!editing && user?.role === "cliente" && (
              <div>
                <label className="text-xs font-semibold text-[#64748B]">¿Cómo entregás el paquete al courier? *</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setForm({ ...form, modo_entrega_origen: "retiro" })} data-testid="modo-retiro-btn"
                    className={`rounded-lg py-2.5 text-sm font-semibold border transition-colors ${form.modo_entrega_origen === "retiro" ? "bg-[#F5A623] text-white border-[#F5A623]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                    Solicitar retiro
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, modo_entrega_origen: "deposito" })} data-testid="modo-deposito-btn"
                    className={`rounded-lg py-2.5 text-sm font-semibold border transition-colors ${form.modo_entrega_origen === "deposito" ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                    Depositar en sucursal
                  </button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Tipo de bulto</label>
                <select className={input} value={form.tipo_paquete} onChange={set("tipo_paquete")} data-testid="envio-tipo-paquete-select">
                  {TIPOS_PAQUETE.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Contenido del paquete</label>
                <input className={input} value={form.contenido} onChange={set("contenido")} placeholder="Ej. ropa, calzados" data-testid="envio-contenido-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Tipo de servicio</label>
              <select className={input} value={form.tipo_servicio} data-testid="envio-servicio-select"
                onChange={(e) => { const ts = e.target.value; setForm({ ...form, tipo_servicio: ts, costo_envio: editing ? form.costo_envio : String(ts === "prioritario" ? 80000 : 40000) }); }}>
                <option value="normal">Normal — flete base Gs. 40.000</option>
                <option value="prioritario">Prioritario — flete base Gs. 80.000</option>
              </select>
            </div>
            {form.tipo_servicio === "prioritario" && (
              <div>
                <label className="text-xs font-semibold text-[#64748B]">Horario de entrega (prioritario)</label>
                <select className={input} value={form.horario_entrega} onChange={set("horario_entrega")} data-testid="envio-horario-select">
                  <option value="">Sin preferencia</option>
                  {HORARIOS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-[#64748B]">Modalidad de pago</label>
              <select className={input} value={form.modalidad} onChange={set("modalidad")} data-testid="envio-modalidad-select">
                {MODALIDADES.map((m) => <option key={m.id} value={m.id}>{m.label} — {m.desc}</option>)}
              </select>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm flex justify-between">
              <span className="text-[#64748B]">Cobro al destinatario</span>
              <span className="font-bold text-[#0A1F44]">{formatGs(
                form.modalidad === "cod" ? (Number(form.valor_producto)||0)+(Number(form.costo_envio)||0) :
                form.modalidad === "destino" ? (Number(form.costo_envio)||0) :
                (form.modalidad === "envio_pagado" || form.modalidad === "credito") ? (Number(form.valor_producto)||0) : 0
              )}</span>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-[#0A1F44] cursor-pointer">
                <input type="checkbox" checked={form.seguro} onChange={(e) => setForm({ ...form, seguro: e.target.checked })} data-testid="envio-seguro-check" className="h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
                Declarar valor y contratar seguro (prima 3,5%)
              </label>
              {form.seguro && (
                <div className="flex items-center gap-3" data-testid="envio-seguro-fields">
                  <input type="number" min="0" className={input} placeholder="Valor declarado (Gs.)" value={form.valor_declarado} onChange={set("valor_declarado")} data-testid="envio-valor-declarado-input" required />
                  <span className="text-xs font-semibold text-[#64748B] whitespace-nowrap" data-testid="envio-prima-preview">Prima: {formatGs((Number(form.valor_declarado) || 0) * 0.035)}</span>
                </div>
              )}
            </div>
            {!editing && (
              <label className="flex items-start gap-2 text-xs text-[#0A1F44] bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={declaracion} onChange={(e) => setDeclaracion(e.target.checked)} required data-testid="envio-declaracion-check" className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
                <span>Declaro que toda la información es verdadera, incluido el <b>contenido de los paquetes</b>.</span>
              </label>
            )}
            <DialogFooter>
              <button type="submit" disabled={loading} data-testid="envio-submit-btn" className="w-full rounded-lg bg-[#0A1F44] hover:bg-[#132D5C] text-white font-semibold py-2.5 text-sm disabled:opacity-60">
                {loading ? "Guardando..." : editing ? "Guardar cambios" : "Crear boleta"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success modal */}
      <ComprobanteModal open={!!comp} envio={comp} onClose={() => setComp(null)} onConfirm={confirmDelivery} />
      <ConfirmFlete envio={cf} onClose={() => setCf(null)} onDone={load} />
      <IncidenciaModal envio={inc} onClose={() => setInc(null)} onDone={load} />
      <ResolverIncidenciaModal envio={resol} onClose={() => setResol(null)} onDone={load} canAlmacenar={isStaff} />
      <Dialog open={!!asignarEnv} onOpenChange={() => setAsignarEnv(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">Asignar repartidor · {asignarEnv?.code}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {repartidores.map((u) => (
              <button key={u.id} onClick={() => doAsignar(u.id)} data-testid={`assign-rep-${u.id}`} className="w-full text-left px-4 py-3 rounded-lg border border-slate-200 hover:border-[#F5A623] hover:bg-amber-50/40">
                <div className="font-semibold text-[#0A1F44]">{u.name}</div>
                <div className="text-xs text-[#64748B]">{u.role}{u.sucursal ? ` · ${u.sucursal}` : ""}</div>
              </button>
            ))}
            {repartidores.length === 0 && <p className="text-sm text-[#64748B] text-center py-6">No hay repartidores disponibles. Creálos en Usuarios y Roles.</p>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!success} onOpenChange={() => setSuccess(null)}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader><DialogTitle className="text-center font-display">¡Boleta creada!</DialogTitle></DialogHeader>
          {success && (
            <div className="space-y-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                <span className="text-2xl">✓</span>
              </div>
              <div className="font-mono font-bold text-xl text-[#0A1F44]">{success.code}</div>
              <div className="text-sm text-[#64748B]">
                Enlace público de tracking:
                <a href={`/track/${success.code}`} target="_blank" rel="noreferrer" className="block text-[#3B82F6] font-medium break-all mt-1">{trackUrl(success.code)}</a>
              </div>
              {success.status === "solicitado" && success.sucursal_origen && success.modo_entrega_origen === "retiro" && (
                <button onClick={() => abrirSolicitarRetiro(success)} data-testid="success-solicitar-retiro-btn" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] hover:bg-[#e09512] text-white font-semibold py-2.5 text-sm">
                  <Truck className="h-4 w-4" /> Solicitar retiro a domicilio
                </button>
              )}
              {success.status === "solicitado" && success.modo_entrega_origen === "deposito" && (
                <div className="text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-3">
                  Llevá tu paquete a la sucursal <b>{success.sucursal_origen}</b> de Pya'elino Courier para despacharlo.
                </div>
              )}
              {success.status === "retirado" && (
                <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3" data-testid="success-retirado-note">
                  Boleta registrada como <b>RETIRADA</b>. Depositá el paquete en la sucursal <b>{success.sucursal_origen}</b> para su despacho; podés depositar varias boletas a la vez desde <b>Mi Ruta</b>.
                </div>
              )}
              {success.flete_confirmado ? (
                <button onClick={() => notify(success)} data-testid="success-wa-btn" className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 text-sm">
                  <MessageCircle className="h-4 w-4" /> Avisar al destinatario por WhatsApp
                </button>
              ) : (
                <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3">
                  El aviso por WhatsApp al destinatario estará disponible cuando el repartidor retire el paquete o se deposite en la sucursal de origen y se confirme el flete.
                </div>
              )}
              <button onClick={() => setSuccess(null)} className="w-full rounded-lg border border-slate-300 py-2 text-sm font-medium">Cerrar</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cambioReview} onOpenChange={(o) => { if (!o) setCambioReview(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="cambio-review-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Cambios solicitados · {cambioReview?.code}</DialogTitle>
            <DialogDescription>Revisá y aprobá o rechazá los cambios pedidos por el cliente.</DialogDescription>
          </DialogHeader>
          {cambioReview && (
            <div className="space-y-3">
              {cambioReview.cambio_solicitado?.motivo && (
                <p className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-2"><b>Motivo:</b> {cambioReview.cambio_solicitado.motivo}</p>
              )}
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {Object.entries(cambioReview.cambio_solicitado?.campos || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-2 text-sm px-3 py-2" data-testid={`cambio-campo-${k}`}>
                    <span className="text-[#64748B]">{CAMBIO_LABELS[k] || k}</span>
                    <span className="text-right"><span className="line-through text-slate-400">{fmtCambio(k, cambioReview[k])}</span> → <b className="text-[#0A1F44]">{fmtCambio(k, v)}</b></span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#64748B]">Solicitado por {cambioReview.cambio_solicitado?.solicitado_por}</p>
              <div className="flex gap-2">
                <button onClick={rechazarCambio} data-testid="cambio-rechazar-btn" className="flex-1 rounded-lg border border-rose-300 text-rose-600 font-semibold py-2 text-sm hover:bg-rose-50">Rechazar</button>
                <button onClick={aprobarCambio} data-testid="cambio-aprobar-btn" className="flex-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2 text-sm">Aprobar y aplicar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!retiroModal} onOpenChange={(o) => { if (!o) setRetiroModal(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Solicitar retiro a domicilio</DialogTitle>
            <DialogDescription>Boleta {retiroModal?.env?.code} · Sucursal {retiroModal?.env?.sucursal_origen}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {retiroModal?.fueraHorario && (
              <label className="flex items-start gap-2 rounded-lg border-2 border-rose-300 bg-rose-50 p-3 cursor-pointer" data-testid="retiro-horario-warn">
                <input type="checkbox" checked={aceptaHorario} onChange={(e) => setAceptaHorario(e.target.checked)} data-testid="retiro-acepta-horario-check" className="mt-0.5 h-4 w-4 rounded border-rose-400 accent-rose-600" />
                <span className="text-sm text-rose-700 font-semibold">Solicitud FUERA DE HORARIO (corte {cutoffText(retiroModal.env.sucursal_origen)} en {retiroModal.env.sucursal_origen}). Entiendo que es posible que el retiro se realice al día siguiente.</span>
              </label>
            )}
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
              <input type="checkbox" checked={aceptaCosto} onChange={(e) => setAceptaCosto(e.target.checked)} data-testid="retiro-acepta-costo-check" className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0A1F44]" />
              <span className="text-sm text-[#0A1F44] font-medium">Tengo listo para entregar el envío y estoy atento. Acepto pagar el costo del recorrido si hago esperar al repartidor o no puedo entregar.</span>
            </label>
            <button
              onClick={enviarSolicitudRetiro}
              disabled={!aceptaCosto || (retiroModal?.fueraHorario && !aceptaHorario)}
              data-testid="retiro-enviar-btn"
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] hover:bg-[#e09512] text-white font-semibold py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              <MessageCircle className="h-4 w-4" /> Enviar solicitud a la sucursal por WhatsApp
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
